import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CoachingPracticeLine,
  CoachingPracticeSpeaker,
} from '@euler/model/coaching-practice';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { memo, useRef } from 'react';
import { ScrollView, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { aiColors } from '../../../../../ai-suite/theme';
import { TraineeVoice } from '../../../functions/traineeVoice';
import {
  LinePosition,
  LineReveal,
  LineVoice,
} from '../../../functions/useScenePlayback';
import { CustomerBubble } from './CustomerBubble';
import { ScenePill } from './ScenePill';
import { TraineeBubble } from './TraineeBubble';
import { TraineeDraftBubble, VoiceDraft } from './TraineeDraftBubble';

/**
 * The conversation as a chat thread under a pill that sets the scene;
 * it follows the newest line while the trainee is at the bottom.
 */
export const PracticeThread = memo(
  ({
    lines,
    streaming,
    replying,
    scenePill,
    openingTask,
    customerName,
    playingTurn,
    reveal$,
    voices$,
    position$,
    canPlay,
    onPlay,
    draft,
    level$,
    traineeVoices,
    traineePlaying,
    onPlayTrainee,
  }: {
    lines: CoachingPracticeLine[];
    /** The customer's reply while it streams. */
    streaming: string;
    /** A line is on its way: the customer is about to answer. */
    replying: boolean;
    scenePill: string;
    /** The trainee opens: what they are about to do. */
    openingTask: string | null;
    customerName: string;
    /** The customer speaks (not on WeChat). */
    playingTurn: number | null;
    /**
     * The line being voiced shows only what has been heard of it; read
     * here, so its ticks redraw the thread and not the scene.
     */
    reveal$: BehaviorSubject<LineReveal | null>;
    voices$: BehaviorSubject<ReadonlyMap<number, LineVoice>>;
    position$: BehaviorSubject<LinePosition | null>;
    canPlay: (turn: number) => boolean;
    onPlay: (turn: number) => void;
    /** The trainee's line while spoken; null when not talking. */
    draft: VoiceDraft | null;
    /** The trainee's voice level while talking, for the draft's meter. */
    level$: BehaviorSubject<number>;
    /** How each spoken trainee line sounded; a typed one has none. */
    traineeVoices: ReadonlyMap<CoachingPracticeLine, TraineeVoice>;
    /** The trainee line being heard back. */
    traineePlaying: CoachingPracticeLine | null;
    onPlayTrainee: (line: CoachingPracticeLine, voice: TraineeVoice) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const scroll = useRef<ScrollView>(null);
    // Follows new lines unless the trainee scrolled up to read back.
    const following = useRef(true);
    const [reveal] = useBehaviorSubject(reveal$);
    const [voices] = useBehaviorSubject(voices$);
    const heardOf = (text: string, turn: number | null) =>
      reveal && reveal.turn === turn ? text.slice(0, reveal.shown) : text;
    // The reply is on its way until the customer's line is in.
    const awaiting =
      replying &&
      lines[lines.length - 1]?.speaker === CoachingPracticeSpeaker.Trainee;
    const liveTurn = reveal?.turn ?? null;
    // A spoken line with its audio kept plays back; any other has no button.
    const playTrainee = (line: CoachingPracticeLine) => {
      const voice = traineeVoices.get(line);
      return voice?.audio ? () => onPlayTrainee(line, voice) : null;
    };
    return (
      <ScrollView
        ref={scroll}
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: rpx(16),
          paddingTop: rpx(16),
          paddingBottom: rpx(24),
          gap: rpx(16),
        }}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
        scrollEventThrottle={100}
        onScroll={({ nativeEvent: x }) => {
          following.current =
            x.contentOffset.y + x.layoutMeasurement.height >=
            x.contentSize.height - rpx(80);
        }}
        onContentSizeChange={() => {
          if (following.current) scroll.current?.scrollToEnd();
        }}
      >
        <View style={{ gap: rpx(8) }}>
          <ScenePill text={scenePill} />
          {!lines.length && openingTask ? (
            <Typography.Text
              size={rpx(13)}
              color={aiColors.mutedForeground}
              style={{ textAlign: 'center' }}
            >
              {openingTask}
            </Typography.Text>
          ) : null}
        </View>
        {lines.map((line, i) =>
          line.speaker === CoachingPracticeSpeaker.Trainee ? (
            <TraineeBubble
              key={`${line.turn}-t-${i}`}
              text={line.text}
              voice={traineeVoices.get(line) ?? null}
              playing={traineePlaying === line}
              onPlay={playTrainee(line)}
            />
          ) : (
            <CustomerBubble
              key={`${line.turn}-c-${i}`}
              name={customerName}
              text={heardOf(line.text, line.turn)}
              turn={line.turn}
              playing={playingTurn === line.turn}
              voicing={reveal?.turn === line.turn}
              voice={voices.get(line.turn) ?? null}
              position$={position$}
              onPlay={canPlay(line.turn) ? onPlay : null}
            />
          ),
        )}
        {streaming || awaiting ? (
          <CustomerBubble
            name={customerName}
            text={reveal ? streaming.slice(0, reveal.shown) : streaming}
            turn={liveTurn ?? -1}
            playing={liveTurn != null && playingTurn === liveTurn}
            voicing={!!reveal}
            voice={liveTurn != null ? voices.get(liveTurn) ?? null : null}
            position$={position$}
            onPlay={null}
          />
        ) : null}
        {draft ? <TraineeDraftBubble draft={draft} level$={level$} /> : null}
      </ScrollView>
    );
  },
);
